import { useFlagPart } from "./flaggedPartHooks";
import { useToast } from "@/hooks/useToast";
import { ConfirmAlert, ErrorAlert } from "@/utils/alert";
import { getAxiosErrorMessage } from "@/utils/healper";

interface PartWithFlag {
  part_id?: number;
  is_flagged?: boolean;
}

interface UseFlagPartHandlerOptions {
  onOpenUnflagModal: (partId: number) => void;
}

/**
 * Custom hook สำหรับจัดการการ Flag/Unflag Part
 * รองรับทั้งกรณีที่รับ partInfo เป็น parameter และกรณีที่ใช้ partId โดยตรง
 *
 * @param options - configuration options
 * @returns Object ที่มี handleFlagPart function และ isFlagging status
 *
 * @example
 * // กรณีที่ใช้กับ Table Row (รับ parameter)
 * const { handleFlagPart, isFlagging } = useFlagPartHandler({
 *   onOpenUnflagModal: (partId) => {
 *     setFlagPartId(partId);
 *     setIsOpenUnFlagModal(true);
 *   }
 * });
 * // ใช้ในฟังก์ชัน: handleFlagPart(partInfo)
 *
 * @example
 * // กรณีที่ใช้กับ part เดี่ยว (ไม่รับ parameter)
 * const { handleFlagPartById, isFlagging } = useFlagPartHandler({
 *   onOpenUnflagModal: () => setIsOpenUnFlagModal(true)
 * });
 * // ใช้ในฟังก์ชัน: handleFlagPartById(partId, isFlagged)
 */
export const useFlagPartHandler = (options: UseFlagPartHandlerOptions) => {
  const { onOpenUnflagModal } = options;
  const { mutate: flagPart, isPending: isFlagging } = useFlagPart();
  const { showToast } = useToast();

  /**
   * จัดการการ Flag Part โดยรับ partInfo object (สำหรับใช้กับ Table Row)
   * @param partInfo - Object ที่มี part_id และ is_flagged
   * @returns Function ที่ใช้เป็น onClick handler
   */
  const handleFlagPart = (partInfo: PartWithFlag) => () => {
    const partId = partInfo?.part_id || 0;
    const isFlagged = partInfo?.is_flagged || false;

    if (!isFlagged) {
      // Flag Part
      ConfirmAlert(
        "Confirm",
        "ต้องการ Flag Part ใช่หรือไม่?",
        "ยืนยัน",
        "ยกเลิก",
      ).then((result) => {
        if (result) {
          flagPart(
            {
              partId,
              isFlagged: true,
              flaggedReason: new Set(["Manual Flag"]),
            },
            {
              onSuccess: () => {
                showToast("Flag Part สำเร็จ", "success");
              },
              onError: (error) => {
                console.error("Error flagging part:", error);
                ErrorAlert("Error", getAxiosErrorMessage(error));
              },
            },
          );
        }
      });
    } else {
      // Unflag Part - เปิด modal
      onOpenUnflagModal(partId);
    }
  };

  /**
   * จัดการการ Flag Part โดยรับ partId และ isFlagged โดยตรง
   * @param partId - ID ของ part
   * @param isFlagged - สถานะการ flag ปัจจุบัน
   */
  const handleFlagPartById = (partId: number, isFlagged: boolean) => {
    if (!isFlagged) {
      // Flag Part
      ConfirmAlert(
        "Confirm",
        "ต้องการ Flag Part ใช่หรือไม่?",
        "ยืนยัน",
        "ยกเลิก",
      ).then((result) => {
        if (result) {
          flagPart(
            {
              partId,
              isFlagged: true,
              flaggedReason: new Set(["Manual Flag"]),
            },
            {
              onSuccess: () => {
                showToast("Flag Part สำเร็จ", "success");
              },
              onError: (error) => {
                console.error("Error flagging part:", error);
                ErrorAlert("Error", getAxiosErrorMessage(error));
              },
            },
          );
        }
      });
    } else {
      // Unflag Part - เปิด modal
      onOpenUnflagModal(partId);
    }
  };

  return {
    handleFlagPart,
    handleFlagPartById,
    isFlagging,
  };
};
